import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

import { CsvSchema, CuttingCustomGrooveSchema, GrooveTypeEnum } from 'cloud-shared-lib';
import { customResponse, ICustomResponse } from '../../helpers/pipeable-operators.helper';
import { CuttingClientOptions } from '../../models/cutting/cutting-client.model';
import { CuttingCustomHoleSchema } from '../../models/cutting/cutting-custom-hole-schema.model';
import { CuttingFastenersSet } from '../../models/cutting/cutting-fasteners-set.model';
import { CuttingPanelCsvInfo } from '../../models/cutting/cutting-panel-csv.model';
import { CuttingPanelSlot } from '../../models/cutting/cutting-panel-slot.model';
import { CuttingStandardCutoutSlot } from '../../models/cutting/cutting-standard-cutout-slot.model';
import { CuttingStandardCutout } from '../../models/cutting/cutting-standard-cutout.model';
import { CuttingSettings } from '../../models/user/cutting-settings.model';
import { ApiLinks } from './csl.api-links';

export interface CustomSlotsOption {
  inPlate: boolean;
  inSide: boolean;
  rabbet: boolean;
  chamfer: boolean;
  fillet: boolean;
  chamfer2: boolean;
}

@Injectable()
export class CuttingPanelListService {
  onClientOptionsChanged: BehaviorSubject<CuttingClientOptions> = new BehaviorSubject(null);
  onCuttingOptionsChanged: BehaviorSubject<CuttingSettings> = new BehaviorSubject(null);
  onOrderUsedGroovesChanged = new BehaviorSubject<CuttingPanelSlot[]>([]);

  onPanelFileAttached: Subject<File> = new Subject();

  constructor(private http: HttpClient) {}

  getClientOptions(): Promise<CuttingClientOptions> {
    return new Promise((resolve, reject) => {
      this.http.get(ApiLinks.clientOptions).subscribe((response: CuttingClientOptions) => {
        this.onClientOptionsChanged.next(response);
        resolve(response);
      }, reject);
    });
  }

  saveClientOptions(options: CuttingClientOptions): Promise<unknown> {
    return new Promise((resolve, reject) => {
      this.http.put(ApiLinks.clientOptions, options).subscribe(() => {
        this.onClientOptionsChanged.next(options);
        resolve(undefined);
      }, reject);
    });
  }

  getPanelsFromCsv(file: File, schemaId: number): Promise<CuttingPanelCsvInfo[]> {
    const formData = new FormData();
    formData.append('attachmentFile', file, file.name);

    const httpParams = new HttpParams().set('schemaId', schemaId.toString());

    return new Promise((resolve, reject) => {
      this.http
        .post(ApiLinks.csv2panels, formData, { params: httpParams })
        .subscribe((response: CuttingPanelCsvInfo[]) => {
          resolve(response);
        }, reject);
    });
  }

  getPanelAttachmentFileC(orderId: number, fileId: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      this.http
        .get(`${ApiLinks.orderAttachmentsC(orderId)}/file/${fileId}`, {
          responseType: 'blob',
          observe: 'response',
        })
        .subscribe(response => {
          resolve(response.body);
        }, reject);
    });
  }

  getPanelAttachmentFileU(orderId: number, fileId: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      this.http
        .get(ApiLinks.downloadOrderAttachmentsU(orderId, fileId), {
          responseType: 'blob',
          observe: 'response',
        })
        .subscribe(response => {
          resolve(response.body);
        }, reject);
    });
  }

  getCustomHoleSchemas(): Promise<CuttingCustomHoleSchema[]> {
    return new Promise((resolve, reject) => {
      this.http.get(ApiLinks.customHoleSchemas).subscribe((response: CuttingCustomHoleSchema[]) => {
        resolve(response.map(s => _.merge(new CuttingCustomHoleSchema(), s)));
      }, reject);
    });
  }

  getFastenerSets(): Promise<CuttingFastenersSet[]> {
    return new Promise((resolve, reject) => {
      this.http.get(ApiLinks.fastenersSet).subscribe((response: CuttingFastenersSet[]) => {
        resolve(response.map(s => _.merge(new CuttingFastenersSet(), s)));
      }, reject);
    });
  }

  getCuttingOptionsFromClientApi(): Promise<CuttingSettings> {
    return new Promise((resolve, reject) => {
      this.http.get(ApiLinks.cuttingSettingsC).subscribe((response: CuttingSettings) => {
        this.onCuttingOptionsChanged.next(response);
        resolve(response);
      }, reject);
    });
  }

  getCuttingOptions(): Promise<CuttingSettings> {
    return new Promise((resolve, reject) => {
      this.http.get(ApiLinks.cuttingSettingsU).subscribe((response: CuttingSettings) => {
        this.onCuttingOptionsChanged.next(response);
        resolve(response);
      }, reject);
    });
  }

  getStandardCutouts(groupId?: number): Observable<ICustomResponse<CuttingStandardCutout[]>> {
    let queryParams: HttpParams = new HttpParams();

    if (groupId) {
      queryParams = queryParams.append('materialGroupId', groupId.toString());
    }

    return this.http
      .get(ApiLinks.cuttingStandardCutouts, { params: queryParams })
      .pipe(customResponse<CuttingStandardCutout[]>());
  }

  getAvailableSlotsStandardCutouts(): Observable<CuttingStandardCutoutSlot[]> {
    return this.http.get<CuttingStandardCutoutSlot[]>(ApiLinks.availableSlotsStandardCutouts);
  }

  getSlots(): Observable<CuttingPanelSlot[]> {
    return this.http.get<CuttingPanelSlot[]>(ApiLinks.grooves);
  }

  getCsvSchemas(): Observable<CsvSchema[]> {
    return this.http.get<CsvSchema[]>(ApiLinks.csvSchemas);
  }

  addCsvSchema(csvSchema: CsvSchema): Observable<CsvSchema> {
    return this.http.post<CsvSchema>(ApiLinks.csvSchemas, csvSchema);
  }

  updateCsvSchema(csvSchema: CsvSchema): Observable<void> {
    return this.http.put<void>(ApiLinks.csvSchemas, csvSchema);
  }

  deleteCsvSchema(id: number): Observable<void> {
    const httpParams = new HttpParams().set('id', id.toString());

    return this.http.delete<void>(ApiLinks.csvSchemas, { params: httpParams });
  }

  getCustomSlotOption(): Promise<CustomSlotsOption> {
    return new Promise((resolve, reject) => {
      this.http.get(ApiLinks.customGrooveOptions).subscribe((response: CustomSlotsOption) => {
        resolve(response);
      }, reject);
    });
  }

  getCustomSlotSchemas(grooveType: GrooveTypeEnum | null): Observable<CuttingCustomGrooveSchema[]> {
    const httpParams = new HttpParams();

    if (!_.isNil(grooveType)) {
      httpParams.append('grooveType', grooveType.toString());
    }

    return this.http.get<CuttingCustomGrooveSchema[]>(ApiLinks.customGrooveSchemas, {
      params: httpParams,
    });
  }

  addCustomSlot(groove: CuttingPanelSlot, schemaId: number): Observable<number> {
    const httpParams = new HttpParams().set('schemaId', schemaId.toString());

    return this.http.post<number>(ApiLinks.grooves, groove, { params: httpParams });
  }

  deleteCustomSlot(grooveId: number): Observable<void> {
    return this.http.delete<void>(ApiLinks.grooves + '/' + grooveId);
  }
}
